<template>
  <div @click="$ev => $emit('click', $ev)">
    <teleport v-if="isOpen" to="body">
      <div
        class="i-modal-backdrop i-confirmation-dialog"
        @vue:mounted="onOpen"
        @vue:before-unmount="onClose"
      >
        <div class="vs-modal__container">
          <div class="vs-modal__header">
            <h2 class="vs-modal__header-text">{{ title }}</h2>
            <img
              class="closeDialogButton"
              alt="dialog close button"
              :src="closeButton"
              @click="closeModal"
            >
          </div>
          <div class="vs-modal__body">
            <slot name="idialog-body"></slot>
          </div>
          <div class="vs-modal__footer">
            <div class="footer-btn-container">
              <i-button
                btn-class="cancel-btn border"
                :is-disabled="isButtonLoading"
                :label="closeBtnLabel"
                @click.stop.prevent="closeModal"
              />
              <i-button
                btn-class="delete-btn border"
                :label="submitBtnLabel"
                :is-disabled="isDisabled"
                :is-loading="isButtonLoading"
                @click.stop.prevent="confirm"
              />
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import {lockBodyScroll, unlockBodyScroll} from '@/utils/bodyScrollLock';
import {activateFocusTrap} from '@/utils/focusTrap';
import IButton from "@/components/common/IButton";

export default {
  name: 'IConfirmationDialog',
  emits: ['click', 'close'],
  components: {IButton},

  props: {
    title: {
      type: String,
    },
    closeBtn: {
      type: String,
      default: null
    },
    submitBtn: {
      type: String,
      default: null
    },
    confirmAction: {
      type: Function,
      required: true,
    },
    closeOnError: {
      type: Boolean,
      default: false
    },
    isButtonLoading: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
  },

  data: () => ({isOpen: false, isLoading: false}),

  computed: {
    closeButton() {
      if (this.$isDark) {
        return require(`../../../assets/app/icons/close-icon.svg`);
      }
      return require(`../../../assets/app/icons/close-icon-dark.svg`);
    },
    closeBtnLabel() {
      return this.closeBtn || this.$t('common.no');
    },
    submitBtnLabel() {
      return this.submitBtn || this.$t('common.yes');
    }
  },

  mounted() {
    document.addEventListener('keydown', this.onEsc);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onEsc);
  },

  created() {
    this.releaseFocusTrap = null;
  },

  methods: {
    onOpen(vnode) {
      this.releaseFocusTrap = activateFocusTrap(vnode?.el);
      lockBodyScroll(this);
    },
    onClose() {
      this.releaseFocusTrap?.();
      this.releaseFocusTrap = null;
      unlockBodyScroll(this);
    },
    openModal() {
      this.isOpen = true;
    },

    closeModal() {
      this.isOpen = false;
      this.$emit('close');
    },

    onEsc(e) {
      if (e.key === 'Escape' && this.isOpen) this.closeModal();
    },

    confirm() {
      this.isLoading = true;
      return this.confirmAction()
        .then(() => {
          this.isOpen = false;
        })
        .catch(() => {
          if (this.closeOnError) {
            this.isOpen = false;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  }
}
</script>

<style lang="scss">
@import "IConfirmationDialog";
</style>
